import { OfferError } from './offer-errors'

/**
 * Offer amounts exactly as the API computes them in build_handler, so the
 * buyer can check the quote before committing it into the escrow it signs.
 */

/** Bid rebate in basis points (offers.rs BID_REBATE_BPS). */
export const BID_REBATE_BPS = 169

/**
 * The smallest market fee a bid can end up funding after its rebate: the
 * 1,000-sat minimum applies to the standard fee, the rebate then comes off
 * it, and the fee output only has to stay clear of dust.
 */
export const BID_FEE_NET_MIN = 546

/** (standard market fee, rebate, fee the buyer funds) — mirrors the API's `bid_fee_split`. */
export function bidFeeSplit(priceSats: number, fullFeeSats: number) {
	const wanted = Math.floor((priceSats * BID_REBATE_BPS) / 10_000)
	const room = Math.max(0, fullFeeSats - BID_FEE_NET_MIN)
	const rebate = Math.max(0, Math.min(wanted, room))
	return { fullFee: fullFeeSats, rebate, fee: fullFeeSats - rebate }
}

/** Validity the API clamps a build to (offers.rs MIN/MAX_VALIDITY_DAYS). */
const MIN_VALIDITY_DAYS = 1
const MAX_VALIDITY_DAYS = 365
const BLOCKS_PER_DAY = 144
/** Settlement budgets the API prices (offers.rs ACCEPT_VBYTES, FILL_VBYTES, offer_mint_vbytes). */
const ACCEPT_VBYTES = 330
const FILL_VBYTES = 380
const OFFER_MINT_VBYTES = 41
/** How far the quoted expiry may sit from now + validity: clock skew and a slow build. */
const EXPIRY_TOLERANCE_MS = 86_400_000

/**
 * The standard market fee on a price, exactly the API's: legacy_price_split
 * (MARKET_ROYALTY 0.027 grossed up from the price in IEEE doubles and
 * truncated, MARKET_FEE_MIN 1,000 tested on the gross), or 270/9730 with a
 * 1,000-sat floor for a NAT lot. The buyer funds bidFeeSplit(...).fee of it.
 */
export function standardOfferFeeSats(priceSats: number, nat: boolean): number {
	if (nat) return Math.max(Number((BigInt(priceSats) * BigInt(270)) / BigInt(9730)), 1000)
	const royalty = 0.027
	const gross = priceSats + Math.floor((priceSats * royalty) / (1 - royalty))
	return gross * royalty < 1000 ? 1000 : gross - priceSats
}

export interface OfferQuote {
	price_sats: number
	market_fee_sats: number
	network_fee_sats: number
	escrow_value: number
	recovery_delay_blocks: number
	expires_at: string
}

/** What build_handler quotes for this price, scope, fee rate and validity. */
export function expectedOfferQuote(
	params: { priceSats: number; feeRate: number; validityDays: number; nat?: boolean },
	scope: 'item' | 'collection' | 'trait',
	now = Date.now()
) {
	const price = Math.round(params.priceSats)
	const days = Math.min(Math.max(Math.trunc(params.validityDays), MIN_VALIDITY_DAYS), MAX_VALIDITY_DAYS)
	const fee = bidFeeSplit(price, standardOfferFeeSats(price, !!params.nat)).fee
	const networkFee = Math.ceil(
		((scope === 'item' ? ACCEPT_VBYTES : FILL_VBYTES) + OFFER_MINT_VBYTES) * params.feeRate
	)
	return {
		price_sats: price,
		market_fee_sats: fee,
		network_fee_sats: networkFee,
		escrow_value: price + fee + networkFee,
		recovery_delay_blocks: Math.min(days * BLOCKS_PER_DAY, 65_535),
		expires_at_ms: now + days * 86_400_000
	}
}

/**
 * The quote must be the one this offer produces, mirroring build_handler: a
 * service that inflated the market fee, the settlement budget or the escrow,
 * shortened the buyer's recovery delay, or moved the expiry would otherwise
 * have those terms committed into the escrow the buyer signs.
 */
export function validateOfferQuote(
	build: OfferQuote,
	params: { priceSats: number; feeRate: number; validityDays: number; natTerms?: unknown },
	scope: 'item' | 'collection' | 'trait',
	now = Date.now()
): void {
	const expected = expectedOfferQuote({ ...params, nat: !!params.natTerms }, scope, now)
	const expiry = Date.parse(build.expires_at)
	if (
		build.price_sats !== expected.price_sats ||
		build.market_fee_sats !== expected.market_fee_sats ||
		build.network_fee_sats !== expected.network_fee_sats ||
		build.escrow_value !== expected.escrow_value ||
		build.recovery_delay_blocks !== expected.recovery_delay_blocks ||
		!Number.isFinite(expiry) ||
		Math.abs(expiry - expected.expires_at_ms) > EXPIRY_TOLERANCE_MS
	)
		throw new OfferError('offer_terms_invalid')
}
